import * as clientSideStored from '@/util/client-side-stored'

export const piniaStores = shallowReactive(new Map<string, () => any>())

/**
 * The `defineStepStore` wraps Pinia's `defineStore` and passes through its result,
 * with some added bonuses:
 * - defined step stores are available via `useStepStore(key)`
 * - a step store callback is required to have a `fields` property,
 *   which is an array with configurator fields belonging to the step
 * - the resulting step store additional has
 *   - a boolean `isValid` property
 *   - a boolean `isDirty` property
 *   - a `reset()` function to reset all fields to their initial values
 *   - a `commit()` function to update all initial values to the current storable values
 *   - a `storableState` property, which is an object with all the storable values of its fields
 *   - all fields as properties by their keys
 *
 * For example:
 * ```
 * defineStepStore('foo', () => {
 *   return {
 *     foo: ref('bar'),
 *     fields: [
 *       useConfiguratorValue('baz', ...),
 *       useConfiguratorValue('qux', ...),
 *     ]
 *   }
 * })
 * ```
 *
 * This will create a store with the following properties:
 * - `foo`: a ref with the value 'bar'
 * - `baz`: the field object for 'baz'
 * - `qux`: the field object for 'qux'
 * - `fields`: the array with the field objects for 'baz' and 'qux'
 * - `isValid`: a computed ref that is true if all fields are valid
 * - `isDirty`: a computed ref that is true if any field is dirty
 * - `storableState`: an object with the storable values of 'baz' and 'qux'
 */
export function defineStepStore<
  T extends keyof OwnbitConfigurator.Steps.StorableState,
  U extends OwnbitConfigurator.Steps.StepStoreCallback<Fields, LiveValue>,
  Fields extends Array<OwnbitConfigurator.BaseConfiguratorValue<OwnbitConfigurator.Key, LiveValue>>,
  LiveValue,
>(key: T, callback: U): U {
  piniaStores.set(
    key,
    defineStore(key, () => {
      const results = callback()

      const isValid = logicAnd(...results.fields.map(field => toRef(field, 'isValid')))
      const isDirty = logicOr(...results.fields.map(field => toRef(field, 'isDirty')))
      const isVisible = logicOr(...results.fields.map(field => toRef(field, 'isVisible')))

      type StoreStorableState = {
        [K in Fields[number] as K['key']]: K['storableValue']
      }
      const storableState = computed(() => {
        return Object.fromEntries(
          results.fields.map(field => [field.key, field.storableValue]),
        ) as StoreStorableState
      })

      const storeResult: any = {}
      for (const key in results) {
        storeResult[key] = computed(() => results[key as keyof typeof results])
      }

      for (const field of results.fields) {
        storeResult[field.key] = field
      }

      function reset() {
        for (const field of results.fields) {
          field.reset()
        }
      }

      function commit() {
        for (const field of results.fields) {
          field.commit()
        }
      }

      Object.assign(storeResult, {
        isValid,
        isDirty,
        isVisible,
        storableState,
        reset,
        commit,
      })

      return storeResult
    }),
  )

  return callback
}

export function useStepStore<T extends keyof OwnbitConfigurator.Steps.Stores>(
  key: T,
): OwnbitConfigurator.Steps.Stores[T] {
  const store = piniaStores.get(key)

  if (!store) {
    throw new Error(`Store for key ${key} not found`)
  }

  return store()
}

/**
 * The `aggregateSteps` store is a Pinia store that aggregates all step stores
 * and provides a single interface to access their properties and some derived values.
 */
export const useAggregateStepsStore = defineStore('aggregateSteps', () => {
  const steps = Object.fromEntries(
    [...piniaStores].map(([key, useStore]) => {
      return [key, useStore()]
    }),
  ) as OwnbitConfigurator.Steps.Stores

  const stepsArray = computed(
    () =>
      Object.values(
        steps,
      ) as OwnbitConfigurator.Steps.Stores[keyof OwnbitConfigurator.Steps.Stores][],
  )

  const isValid = logicAnd(...stepsArray.value.map(store => toRef(store, 'isValid')))
  const isDirty = logicOr(...stepsArray.value.map(store => toRef(store, 'isDirty')))

  function reset() {
    for (const store of stepsArray.value) {
      store.reset()
    }
  }

  function commit() {
    for (const store of stepsArray.value) {
      store.commit()
    }
  }

  const storableState = computed(
    () =>
      Object.assign(
        {},
        ...stepsArray.value.map(store => store.storableState),
      ) as OwnbitConfigurator.AggregateStorableState,
  )

  return {
    steps,
    isValid,
    isDirty,
    reset,
    commit,
    storableState,
  }
})
